import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/hooks/use-toast";
import teamService, { type TeamItem } from "@/services/team.service";
import { mensagemDeErro } from "@/lib/team-errors";

interface TeamFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** `null` cria; um time edita. */
  team: TeamItem | null;
  onSaved: () => void;
}

const LIMITE_NOME = 255;
const LIMITE_DESCRICAO = 500;

/**
 * O formulário do time — os campos que existem sem depender de vínculo nenhum.
 *
 * Membros e lojas ficam de fora de propósito: um time vazio é estado legítimo, e a agência
 * costuma criar o time antes de saber quem entra nele. A composição é definida na tela do
 * time, onde a lista inteira de funcionários e de lojas cabe.
 *
 * `is_active` só aparece na edição, e desativar não é excluir: o time desativado guarda a
 * composição e some do alcance dos membros na requisição seguinte — é a forma reversível de
 * tirar acesso, e a que a agência costuma querer.
 */
export const TeamFormDialog = ({
  open,
  onOpenChange,
  team,
  onSaved,
}: TeamFormDialogProps) => {
  const [nome, setNome] = useState("");
  const [descricao, setDescricao] = useState("");
  const [ativo, setAtivo] = useState(true);
  const [salvando, setSalvando] = useState(false);

  const editando = team !== null;

  useEffect(() => {
    if (!open) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setNome(team?.name ?? "");
    setDescricao(team?.description ?? "");
    setAtivo(team?.is_active ?? true);
  }, [open, team]);

  const salvar = async () => {
    const nomeAparado = nome.trim();
    if (!nomeAparado) {
      toast({
        title: "Informe o nome do time.",
        variant: "destructive",
      });
      return;
    }

    setSalvando(true);
    try {
      if (team) {
        await teamService.update(team.id, {
          name: nomeAparado,
          // `null` **apaga** a descrição nesta rota, que é o que o campo vazio quer dizer.
          description: descricao.trim() || null,
          is_active: ativo,
        });
        toast({ title: "Time atualizado." });
      } else {
        await teamService.create({
          name: nomeAparado,
          description: descricao.trim() || null,
        });
        toast({ title: "Time criado." });
      }
      onOpenChange(false);
      onSaved();
    } catch (error) {
      toast({
        title: editando ? "Erro ao atualizar o time" : "Erro ao criar o time",
        description: mensagemDeErro(error, "Tente novamente."),
        variant: "destructive",
      });
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editando ? "Editar time" : "Novo time"}</DialogTitle>
          <DialogDescription>
            {editando
              ? "Altere o nome, a descrição ou o estado do time."
              : "Crie o time agora e defina os membros e as lojas em seguida."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="team-name">Nome</Label>
            <Input
              id="team-name"
              value={nome}
              maxLength={LIMITE_NOME}
              onChange={(event) => setNome(event.target.value)}
              placeholder="Ex.: Performance"
            />
            <p className="text-xs text-muted-foreground">
              Dois times da mesma agência não podem ter o mesmo nome.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="team-description">Descrição (opcional)</Label>
            <Textarea
              id="team-description"
              value={descricao}
              maxLength={LIMITE_DESCRICAO}
              onChange={(event) => setDescricao(event.target.value)}
              placeholder="O que este time atende"
              rows={3}
            />
            <p className="text-xs text-muted-foreground">
              {descricao.length}/{LIMITE_DESCRICAO}
            </p>
          </div>

          {editando && (
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div>
                <Label htmlFor="team-active">Time ativo</Label>
                <p className="text-xs text-muted-foreground">
                  Desativar tira o acesso dos membros às lojas do time sem
                  desfazer a composição.
                </p>
              </div>
              <Switch id="team-active" checked={ativo} onCheckedChange={setAtivo} />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={salvando}
          >
            Cancelar
          </Button>
          <Button onClick={salvar} disabled={salvando}>
            {salvando && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {editando ? "Salvar" : "Criar time"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TeamFormDialog;
